import { useState } from "react";
import { motion } from "framer-motion";
import { Theater, ArrowRight, MessageSquare } from "lucide-react";
import { useLang } from "../../context/LanguageContext";

export const ScenarioBlock = ({ block, onDone }) => {
  const { t } = useLang();
  const [step, setStep] = useState(0);
  const [picks, setPicks] = useState([]);
  const [feedback, setFeedback] = useState(null);

  const current = block.steps[step];
  const maxScore = block.steps.reduce((sum, s) => sum + Math.max(...s.options.map((o) => o.score || 0)), 0);
  const earned = picks.reduce((sum, p, i) => sum + (block.steps[i].options[p]?.score || 0), 0);
  const finished = step >= block.steps.length;

  const choose = (i) => {
    setPicks([...picks, i]);
    setFeedback(current.options[i].feedback || "");
  };

  const advance = () => {
    setFeedback(null);
    setStep(step + 1);
  };

  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="card-dark p-7 sm:p-10" data-testid="learn-block-scenario">
      <span className="badge-lime inline-flex items-center gap-2"><Theater className="w-3.5 h-3.5" /> {t("learn.roleplay")}</span>
      {block.role && <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#1D4ED8] mt-5">{block.role}</p>}
      {block.intro && <p className="text-base text-[#334155] mt-3 leading-relaxed whitespace-pre-wrap" data-testid="learn-scenario-intro">{block.intro}</p>}

      <div className="h-2 bg-[#EEF4FF] rounded-full mt-7 overflow-hidden">
        <div className="h-full bg-[#1D4ED8] transition-all duration-500" style={{ width: `${(Math.min(step, block.steps.length) / block.steps.length) * 100}%` }} />
      </div>

      {!finished ? (
        <div className="mt-7" data-testid="learn-scenario-step">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#64748B]">{t("learn.decision")} {step + 1}/{block.steps.length}</p>
          <p className="font-heading font-black text-xl sm:text-2xl text-[#0B1B3A] mt-3 leading-snug" data-testid="learn-scenario-prompt">{current.prompt}</p>
          {feedback === null ? (
            <div className="space-y-3 mt-6">
              {current.options.map((opt, i) => (
                <button
                  key={i}
                  data-testid={`learn-scenario-option-${i}`}
                  onClick={() => choose(i)}
                  className="w-full text-left rounded-[18px] border border-[#E2E8F0] px-5 py-4 text-sm font-semibold text-[#1E293B] hover:border-[#1D4ED8] hover:bg-[#EEF4FF] transition-colors duration-200"
                >
                  {opt.text}
                </button>
              ))}
            </div>
          ) : (
            <div className="mt-6">
              <div className="rounded-[20px] bg-[#F8FAFC] border border-[#E2E8F0] p-5" data-testid="learn-scenario-feedback">
                <p className="inline-flex items-center gap-2 text-sm font-bold text-[#0B1B3A]">
                  <MessageSquare className="w-4 h-4 text-[#1D4ED8]" /> {t("learn.consequence")}
                </p>
                <p className="text-sm text-[#475569] mt-2">{feedback}</p>
              </div>
              <button data-testid="learn-scenario-next" onClick={advance} className="btn-blue mt-6">
                {step + 1 >= block.steps.length ? t("learn.debrief") : t("learn.nextDecision")} <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="mt-8" data-testid="learn-scenario-debrief">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#64748B]">{t("learn.debrief")}</p>
          <p className="font-heading font-black text-3xl text-[#0B1B3A] mt-3">{earned}/{maxScore}</p>
          {block.debrief && <p className="text-sm text-[#475569] mt-4 leading-relaxed whitespace-pre-wrap">{block.debrief}</p>}
          <button data-testid="learn-scenario-continue" onClick={() => onDone(picks)} className="btn-blue mt-7">
            {t("learn.continue")} <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </motion.div>
  );
};

export default ScenarioBlock;
